import { useMemo } from "react";
import { gql, useQuery } from "@apollo/client";
import { PortfolioAccount } from "api/money/useGetPortfoliosAccounts";
import { fallbackLanguage } from "i18n";
import { useKeycloak } from "providers/KeycloakProvider";

//with 1 sub portfolio depth
export const PORTFOLIO_BASIC_FIELDS = gql`
  fragment PortfolioBasicFields on Portfolio {
    id
    name
    status
    shortName
    currency {
      securityCode
      amountDecimalCount
    }
    portfolioGroups {
      id
      code
    }
    parentPortfolios {
      id
    }
    profile {
      W8BEndDate: attribute(attributeKey: "portfolio.T24.W8BEndDate") {
        attributeKey
        dateValue
      }
    }
    portfolios {
      id
      name
      status
      shortName
      currency {
        securityCode
        amountDecimalCount
      }
      portfolioGroups {
        id
        code
      }
    }
    accounts {
      accountName: name
    }
    type {
      name
      code
    }
  }
`;

//maximum of 2 sub portfolio depth
export const CONTACT_INFO_QUERY = gql`
  ${PORTFOLIO_BASIC_FIELDS}
  query GetContactInfo($contactId: Long) {
    contact(id: $contactId) {
      id
      externalId
      contactId
      name
      language {
        locale
      }
      tags
      profile {
        testerExternalId: attribute(attributeKey: "contact.T24.TestExternalId") {
          attributeKey
          stringValue
          __typename
        }
        testerFullName: attribute(attributeKey: "contact.T24.TestFullName") {
          attributeKey
          stringValue
          __typename
        }
        USTaxPayer: attribute(attributeKey: "contact.T24.USTaxpayer") {
          attributeKey
          stringValue
          __typename
        }
      }
      representativeTags {
        representatives
      }
      representees(onlyDirectRepresentees: true) {
        name
        id
        contactId
        tags
        portfolios {
          ...PortfolioBasicFields
        }
        representees {
          name
          contactId
        }
      }
      juridical {
        code
      }
      assetManagerPortfolios {
        primaryContact {
          contactId
          name
        }
      }
      portfolios {
        ...PortfolioBasicFields
      }
    }
  }
`;

export enum PortfolioStatus {
  Active = "A",
  Passive = "P",
  Closed = "C",
}

export enum PortfolioGroups {
  CANCEL_ORDER = "CP_CANCEL",
  DEPOSIT = "CP_DEPOSIT",
  WITHDRAW = "CP_WITHDRAWAL",
  TRADE = "CP_TRADING",
  HIDE = "CP_HIDE_PF",
  MONTHLY_INVESTMENTS = "CP_MONTHLYINVESTMENTS",
  MONTHLY_SAVINGS = "CP_MONTHLYSAVINGS",
}

export interface PortfolioGroup {
  id: number;
  code: PortfolioGroups;
}

export interface Representee {
  id: number;
  name: string;
  contactId: string;
  portfolios: Portfolio[];
  representees: [];
  tags?: string;
}

export interface AssetManagerPortfolios {
  primaryContact: {
    contactId: string;
    name: string;
  };
}

export interface Portfolio {
  id: number;
  name: string;
  status: string;
  shortName: string;
  parentPortfolios: {
    id: number;
  }[];
  portfolios: Portfolio[]; //sub portfolios
  currency: {
    securityCode: string;
    amountDecimalCount: number;
  };
  portfolioGroups: PortfolioGroup[];
  accounts: PortfolioAccount[];
  type: {
    name: string;
    code: string;
  };
  profile?: unknown;
}

export interface ContactInfoQuery {
  contact?: {
    id: number;
    externalId: number;
    contactId: string;
    name: string;
    representees: Representee[];
    juridical: {
      code: string;
    }
    assetManagerPortfolios: AssetManagerPortfolios[];
    language: {
      locale: string;
    };
    portfolios?: Portfolio[];
    tags?: string;
    representativeTags?: {
      representatives: []
    },
    profile?: {
      testerExternalId: {
        attributeKey: string;
        stringValue: string;
        __typename: string;
      },
      testerFullName: {
        attributeKey: string;
        stringValue: string;
        __typename: string;
      },
      USTaxPayer?: {
        attributeKey: string;
        stringValue?: string;
        __typename: string;
      }
    }
  }
}


export const useGetContactInfo = (callAPI = false, id?: string | number) => {
  const { linkedContact } = useKeycloak();
  const { loading, error, data, refetch } = useQuery<ContactInfoQuery>(
    CONTACT_INFO_QUERY,
    {
      variables: {
        contactId: id?.toString() || linkedContact?.toString(),
      },
      fetchPolicy: callAPI ? "cache-and-network" : "cache-first",
    }
  );

  const activeAndPassivePortfolios = useMemo(
    () =>
      !data?.contact?.portfolios?.length
        ? []
        : removeClosed(data?.contact?.portfolios),
    [data?.contact?.portfolios]
  );

  const accountsCurrencies: { [key: string]: Set<string> } = {};
  activeAndPassivePortfolios.forEach(portfolio => {
      portfolio.accounts.forEach(account => {
          const [accountName, currency] = account.accountName.split('-');
          if (!accountsCurrencies[accountName]) {
              accountsCurrencies[accountName] = new Set();
          }
          accountsCurrencies[accountName].add(currency);
      });
  });
  const accountsCurrenciesArray: { [key: string]: string[] } = {};
  Object.keys(accountsCurrencies).forEach(accountName => {
      accountsCurrenciesArray[accountName] = Array.from(accountsCurrencies[accountName]);
  });
  
  return {
    loading: loading,
    error: error,
    data: data && {
      contactId: data?.contact?.id,
      externalId: data?.contact?.externalId,
      _contactId: data?.contact?.contactId,
      portfolios: activeAndPassivePortfolios,
      locale: data?.contact?.language?.locale || fallbackLanguage,
      // all contact portfolios have same currency
      portfoliosCurrency:
        data?.contact?.portfolios?.[0]?.currency?.securityCode,
      representees: data?.contact?.representees,
      assetManagerPortfolios: data?.contact?.assetManagerPortfolios,
      name: data?.contact?.name,
      juridicalCode: data?.contact?.juridical?.code,
      tags: data?.contact?.tags,
      representativeTags: data?.contact?.representativeTags,
      accountsCurrencies: accountsCurrenciesArray,
      profile: data?.contact?.profile,
      usTaxPayer: data?.contact?.profile?.USTaxPayer?.stringValue
    },
    refetch,
    companyTag: data?.contact?.tags?.split(',').find(tag => tag.startsWith('Company-'))?.split('-').pop()
  };
};

/**
 * Removes closed portfolios (recursively on all sub portfolios as well).
 * @param portfolios Raw portfolio data from FA
 * @returns active/pending portfolios
 */
export const getPortfolioW8BEndDate = (portfolio: Portfolio): string | null | undefined => {
  const profile = portfolio.profile as { W8BEndDate?: { dateValue?: string | null } } | null | undefined;
  return profile?.W8BEndDate?.dateValue;
};

export const removeClosed = (portfolios: Portfolio[]) => {
  const result = [] as Portfolio[];
  portfolios?.forEach((portfolio) => {
    if (portfolio.status !== PortfolioStatus.Closed) {
      const portfolios = removeClosed(portfolio.portfolios);
      result.push({
        ...portfolio,
        portfolios,
      });
    }
  });
  return result;
};
